/**
 * Which anchor-nav tab should be highlighted, as a pure function of geometry.
 *
 * Extracted from the component so the rule is unit-testable: the previous implementation drove
 * the active tab from an IntersectionObserver callback, which only receives the sections whose
 * intersection CHANGED. The about sections are thousands of pixels tall, so their
 * intersectionRatio sits near 0.04 and never crosses the observer's thresholds — on an upward
 * jump the callback often never fired and the highlight stuck on the previous tab.
 *
 * The rule instead: the active section is the LAST one whose top has passed the bottom edge of
 * the sticky bar. Evaluated against every section every time, so it cannot go stale.
 */

export interface SectionTop {
  id: string;
  /** Viewport-relative top edge, i.e. getBoundingClientRect().top. */
  top: number;
}

/**
 * A few pixels of slack so a section counts as active the moment its heading meets the bar,
 * rather than one frame later — sub-pixel layout means an exact comparison flickers.
 */
export const ACTIVATION_SLACK_PX = 8;

export function pickActiveId(sections: readonly SectionTop[], barBottom: number, atBottom: boolean): string {
  if (sections.length === 0) return '';
  // A short final section can never reach the bar, so pin the last tab at the page bottom.
  if (atBottom) return sections[sections.length - 1].id;

  let active = sections[0].id;
  for (const section of sections) {
    if (section.top - ACTIVATION_SLACK_PX <= barBottom) active = section.id;
  }
  return active;
}
